import { AlertTriangle } from "lucide-react"
import { useState } from "react"
import { Modal } from "@/componentes/ui/Modal"

import type { ErrorPago } from "./ModalPago"
import { formatearImporte } from "./PanelRubros"

/**
 * Modal para aplicar dinero contra un saldo abierto: un abono a una CxP o un cobro
 * a una CxC.
 *
 * ⚠ **Se pre-llena con el SALDO, no con el total.** Pre-llenar el total invita a
 * abonar de más sobre una CxP que ya tiene abonos: el servidor lo rechaza con
 * `MONTO_EXCEDE_SALDO`, y el operador se queda mirando un error que produjo el
 * propio formulario al proponerle una cifra equivocada.
 *
 * El total se muestra igualmente, como referencia. Lo que no hace es proponerlo.
 *
 * ⚠ **Sirve a las dos bandejas a propósito.** `registrar_cobro()` y
 * `registrar_abono()` son la misma operación con distinto signo —aplicar un importe
 * contra un pendiente, rechazando el exceso con `MONTO_EXCEDE_SALDO`— y darles dos
 * modales distintos garantizaba que dentro de seis meses uno propusiera el total y
 * el otro el saldo sin que nadie lo notara. Sólo cambia la palabra: `accion`.
 *
 * ⚠ **Un error no lo cierra**, por lo mismo que `ModalPago`: se pierde el contexto,
 * no el tiempo.
 */

type Props = {
  /** A quién se le cobra o se le paga: el proveedor de la CxP, el cliente de la CxC. */
  contraparte: string
  monto: string
  montoPagado: string
  moneda: "USD" | "BS"
  /** El verbo de esta bandeja. Cambia el título, la etiqueta y el botón. */
  accion?: string
  error?: ErrorPago | null
  onAbonar: (monto: string) => void
  onCerrar: () => void
}

/** Lo que queda por pagar. La única cifra que este modal propone. */
export function saldoPendiente(monto: string, montoPagado: string): string {
  return (Number(monto) - Number(montoPagado)).toFixed(2)
}

export function ModalAbono({
  contraparte,
  monto,
  montoPagado,
  moneda,
  accion = "Abonar",
  error,
  onAbonar,
  onCerrar,
}: Props) {
  const saldo = saldoPendiente(monto, montoPagado)
  const [importe, setImporte] = useState(saldo)
  const etiquetaMonto = `Monto a ${accion.toLowerCase()}`

  return (
    // ⚠ **Un `Modal` de verdad (12/10/2026).** Era un panel dentro de la página que
    // se anunciaba `aria-modal` sin serlo: sin capa, sin Escape y sin foco, en una
    // pantalla de dinero. Escape y la X equivalen a cancelar.
    <Modal abierto titulo={`${accion} a ${contraparte}`} onCerrar={onCerrar} ancho="sm">
      <dl className="mt-2 grid grid-cols-2 gap-1 text-sm">
        <dt style={{ color: "var(--fg-muted)" }}>Total</dt>
        <dd className="text-right tabular-nums" data-testid="total">
          {formatearImporte(monto, moneda, "")}
        </dd>
        <dt style={{ color: "var(--fg-muted)" }}>Ya aplicado</dt>
        <dd className="text-right tabular-nums">{formatearImporte(montoPagado, moneda, "-")}</dd>
        <dt className="font-medium">Saldo</dt>
        <dd className="text-right font-medium tabular-nums" data-testid="saldo">
          {formatearImporte(saldo, moneda, "")}
        </dd>
      </dl>

      <label className="mt-3 block text-sm">
        {etiquetaMonto}
        <input
          aria-label={etiquetaMonto}
          className="campo mt-1 text-right tabular-nums"
          value={importe}
          onChange={(e) => setImporte(e.target.value)}
        />
      </label>

      {/* Dentro, y sin cerrar. Lo tecleado sigue ahí. */}
      {error && (
        <div
          role="alert"
          className="mt-3 flex gap-2 rounded-[var(--radius-control)] border p-2 text-sm"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertTriangle size={16} aria-hidden={true} />
          <span>
            <strong>{error.code}</strong> · {error.message}
          </span>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-[var(--radius-control)] border px-3 py-1.5"
          style={{ borderColor: "var(--border)" }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => onAbonar(importe)}
          className="rounded-[var(--radius-control)] px-3 py-1.5"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          {accion}
        </button>
      </div>
    </Modal>
  )
}
